import ReactDOM from 'react-dom';
import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  ReactNode,
  useContext,
} from 'react';
import styled from 'astroturf/react';
import {
  autoUpdate,
  computePosition,
  flip,
  shift,
  offset,
  size,
} from '@floating-ui/react';
import { Spinner } from 'js/academia_components/transients/spinner';
import { scrollChildIntoView } from '../utils';
import { mod } from 'js/social/shared/SocialEditor/utils';
import { Body } from 'js/design_system/Body';
import { ModalElementContext } from 'js/design_system/Modal';

const MAX_HEIGHT = 400;

const Popup = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  z-index: 100;
  background-color: $ds-white;
  border: 1px solid $ds-gray-light;
  border-radius: 2px;
  width: max-content;
  overflow: auto;
  filter: drop-shadow(0px 24px 38px rgba(0, 0, 0, 0.07))
    drop-shadow(0px 9px 46px rgba(0, 0, 0, 0.06))
    drop-shadow(0px 11px 15px rgba(0, 0, 0, 0.1));
  // The drop-shadow makes Safari very sluggish without notifying about the change.
  will-change: filter;
`;

const Message = styled(Body)`
  padding: 8px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const ListItem = styled.div<{ active?: boolean }>`
  display: flex;
  gap: 8px;
  padding: 8px;
  cursor: pointer;

  &.active {
    background-color: #afccfc;
  }
`;

type Props<T> = {
  anchorTop: number;
  anchorLeft: number;
  items: T[];
  message?: string;
  activeIndex: number;
  loading: boolean;
  keyExtractor: (item: T) => React.Key;
  renderItem: (item: T) => ReactNode;
  onActiveIndexChange: (activeIndex: number) => void;
  onSelect: (item: T) => void;
};

export const Autocomplete = <T,>({
  anchorTop,
  anchorLeft,
  items = [],
  message,
  activeIndex,
  loading,
  keyExtractor,
  renderItem,
  onActiveIndexChange,
  onSelect,
}: Props<T>) => {
  const [activeDiv, setActiveDiv] = useState<HTMLDivElement | null>(null);
  const [popoverDiv, setPopoverDiv] = useState<HTMLDivElement | null>(null);
  const portalContainer = useContext(ModalElementContext)?.current;

  const anchorElement = useMemo(() => {
    return {
      getBoundingClientRect: () => ({
        width: 0,
        height: 14, // Account for line height
        x: anchorLeft,
        y: anchorTop,
        left: anchorLeft,
        right: anchorLeft,
        top: anchorTop,
        bottom: anchorTop,
      }),
    };
  }, [anchorLeft, anchorTop]);

  const updatePosition = useCallback(() => {
    if (!popoverDiv) return;

    computePosition(anchorElement, popoverDiv, {
      placement: 'bottom-start',
      middleware: [
        flip({ mainAxis: false }),
        shift(),
        size({
          apply({ availableWidth, availableHeight, elements }) {
            Object.assign(elements.floating.style, {
              maxWidth: `${availableWidth}px`,
              maxHeight: `${Math.min(availableHeight, MAX_HEIGHT)}px`,
            });
          },
        }),
        offset(4),
      ],
    }).then(({ x, y }) => {
      Object.assign(popoverDiv.style, {
        top: `${y}px`,
        left: `${x}px`,
      });
    });
  }, [anchorElement, popoverDiv]);

  useEffect(() => {
    if (popoverDiv) {
      return autoUpdate(anchorElement, popoverDiv, updatePosition);
    }
  }, [anchorElement, popoverDiv, updatePosition]);

  useEffect(() => {
    scrollChildIntoView(popoverDiv, activeDiv);
  }, [popoverDiv, activeDiv]);

  return ReactDOM.createPortal(
    <Popup ref={(ref) => setPopoverDiv(ref)}>
      {loading ? (
        <Spinner show />
      ) : message ? (
        <Message size="md">{message}</Message>
      ) : (
        <List>
          {items.map((item, index) => {
            const active = mod(activeIndex, items.length) === index;

            return (
              <ListItem
                ref={(ref) => active && setActiveDiv(ref)}
                key={keyExtractor(item)}
                active={active}
                onMouseEnter={() => onActiveIndexChange(index)}
                onClick={() => onSelect(item)}
              >
                {renderItem(item)}
              </ListItem>
            );
          })}
        </List>
      )}
    </Popup>,
    portalContainer ?? document.body,
  );
};
